import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ForceGraph2D from 'react-force-graph-2d';
import * as d3 from 'd3-force';


// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";


// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import TimelineList from "examples/Timeline/TimelineList";
import TimelineItem from "examples/Timeline/TimelineItem";

// API service
import { api } from "../../services/api";
import { useAuth } from "context/AuthContext";

// Componente para renderizar listas paginadas de identificadores
const PaginatedIdentifierList = ({ title, items = [] }) => {
  const [page, setPage] = useState(0);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const handleNextPage = () => setPage(p => Math.min(p + 1, totalPages - 1));
  const handlePrevPage = () => setPage(p => Math.max(p - 1, 0));

  const paginatedItems = items.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

  return (
    <VuiBox mb={2}>
      <VuiTypography variant="h6" color="white" mb={1}>{title}</VuiTypography>
      {paginatedItems.map((item, index) => (
        <VuiTypography key={index} variant="body2" color="text">- {item}</VuiTypography>
      ))}
      {totalPages > 1 && (
        <VuiBox display="flex" justifyContent="space-between" mt={1}>
          <VuiButton variant="outlined" color="info" size="small" onClick={handlePrevPage} disabled={page === 0}>Anterior</VuiButton>
          <VuiTypography variant="caption" color="text">PÃ¡gina {page + 1} de {totalPages}</VuiTypography>
          <VuiButton variant="outlined" color="info" size="small" onClick={handleNextPage} disabled={page === totalPages - 1}>PrÃ³ximo</VuiButton>
        </VuiBox>
      )}
    </VuiBox>
  );
};

const sanitizeCoordinate = (value) => {
  if (value === null || value === undefined) return null;
  const numberValue = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatDisplayDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return date.toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch (error) {
    return date.toISOString();
  }
};

const normalizeGeoLocation = (geoRaw) => {
  if (!geoRaw) return null;

  let parsed = geoRaw;

  if (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) return null;

    try {
      const maybeObject = JSON.parse(trimmed);
      if (maybeObject && typeof maybeObject === "object" && !Array.isArray(maybeObject)) {
        parsed = maybeObject;
      } else {
        return {
          label: trimmed,
          query: trimmed,
          raw: trimmed,
        };
      }
    } catch (error) {
      return {
        label: trimmed,
        query: trimmed,
        raw: trimmed,
      };
    }
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    const value = String(parsed);
    return {
      label: value,
      query: value,
      raw: parsed,
    };
  }

  const city =
    parsed.city ||
    parsed.town ||
    parsed.village ||
    parsed.city_name ||
    parsed.locality ||
    parsed.cityName;
  const region =
    parsed.region ||
    parsed.state ||
    parsed.state_name ||
    parsed.province ||
    parsed.county ||
    parsed.stateName;
  const country =
    parsed.country ||
    parsed.country_name ||
    parsed.countryCode ||
    parsed.country_code ||
    parsed.countryName;
  const latitude = sanitizeCoordinate(
    parsed.latitude ??
    parsed.lat ??
    parsed.latitud ??
    parsed.latDeg ??
    parsed.lat_deg ??
    (Array.isArray(parsed.latlng) ? parsed.latlng[0] : undefined)
  );
  const longitude = sanitizeCoordinate(
    parsed.longitude ??
    parsed.lon ??
    parsed.lng ??
    parsed.long ??
    parsed.lonDeg ??
    parsed.lon_deg ??
    (Array.isArray(parsed.latlng) ? parsed.latlng[1] : undefined)
  );
  const displayName = parsed.display_name || parsed.label || parsed.name || parsed.displayName;

  const labelParts = [city, region, country]
    .map((part) => (part ? String(part).trim() : null))
    .filter(Boolean);

  const label = labelParts.length > 0 ? labelParts.join(", ") : (displayName ? String(displayName) : null);

  let query = null;
  if (latitude !== null && longitude !== null) {
    query = `${latitude},${longitude}`;
  } else if (displayName) {
    query = String(displayName);
  } else if (label) {
    query = label;
  } else if (parsed.query) {
    query = String(parsed.query);
  } else if (parsed.ip || parsed.ipAddress || parsed.ip_address) {
    query = String(parsed.ip || parsed.ipAddress || parsed.ip_address);
  }

  const ipValue =
    parsed.ip ||
    parsed.ipAddress ||
    parsed.ip_address ||
    parsed.hostname ||
    parsed.query ||
    null;

  return {
    label: label || query,
    query,
    latitude,
    longitude,
    city: city ? String(city) : null,
    region: region ? String(region) : null,
    country: country ? String(country) : null,
    ip: ipValue ? String(ipValue) : null,
    raw: parsed,
  };
};

const getGeoFromInteraction = (interaction) => {
  if (!interaction || typeof interaction !== "object") {
    return null;
  }

  const geoValue =
    interaction.geoLocation ??
    interaction.geo_location ??
    interaction.geolocation ??
    null;

  return normalizeGeoLocation(geoValue);
};

const getIpFromInteraction = (interaction) => {
  if (!interaction || typeof interaction !== "object") {
    return null;
  }

  const directIp =
    interaction.ipAddress ??
    interaction.ip_address ??
    interaction.ip ??
    interaction.deviceIp ??
    interaction.device_ip ??
    interaction.clientIp ??
    interaction.client_ip ??
    null;

  if (directIp) {
    return String(directIp);
  }

  const geo = getGeoFromInteraction(interaction);
  return geo?.ip || null;
};

const LocationPreview = ({ location }) => {
  return (
    <VuiBox mt={3}>
      <VuiTypography variant="h6" color="white" mb={1}>
        LocalizaÃ§Ã£o
      </VuiTypography>
      {location ? (
        <>
          <VuiTypography variant="body2" color="text" mb={location.mapEmbedUrl ? 2 : 0}>
            {location.label}
            {location.recordedAtFormatted
              ? ` - Atualizado em ${location.recordedAtFormatted}`
              : null}
          </VuiTypography>
          {location.mapEmbedUrl ? (
            <VuiBox
              sx={{
                position: "relative",
                width: "100%",
                paddingTop: "56.25%",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <iframe
                title="PrÃ©via da localizaÃ§Ã£o"
                src={location.mapEmbedUrl}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </VuiBox>
          ) : null}
          {location.mapLinkUrl ? (
            <VuiButton
              component="a"
              href={location.mapLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="info"
              sx={{ mt: 2 }}
            >
              Ver mapa
            </VuiButton>
          ) : null}
        </>
      ) : (
        <VuiTypography variant="body2" color="text">
          LocalizaÃ§Ã£o nÃ£o disponÃ­vel.
        </VuiTypography>
      )}
    </VuiBox>
  );
};

function DossierDetail() {
  const { fingerprint } = useParams();
  const navigate = useNavigate();
  const { companyScopeQuery } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const graphRef = useRef();

  useEffect(() => {
    if (graphRef.current) {
      // Aumenta a forÃ§a de repulsÃ£o para evitar sobreposiÃ§Ã£o
      graphRef.current.d3Force('charge').strength(-400);
      // Adiciona uma forÃ§a de colisÃ£o para evitar que os nÃ³s se sobreponham
      graphRef.current.d3Force('collide', d3.forceCollide(56)); // Dobro do raio do nÃ³
    }
  }, [dossier]); // Re-aplica quando os dados do dossiÃª (e do grafo) mudam

  useEffect(() => {
    const fetchDossier = async () => {
      setLoading(true);
      try {
        const querySuffix = companyScopeQuery ? `?${companyScopeQuery}` : '';
        const data = await api.get(`/dossier/by-fingerprint/${fingerprint}${querySuffix}`);
        setDossier(data);
      } catch (error) {
        console.error("Erro ao buscar dossiÃª do dispositivo:", error);
        setDossier(null);
      } finally {
        setLoading(false);
      }
    };

    if (fingerprint) {
      fetchDossier();
    }
  }, [fingerprint, companyScopeQuery]);

  const handleGoBack = () => navigate(-1);
  const handleOpenHistoryModal = () => setHistoryModalOpen(true);
  const handleCloseHistoryModal = () => setHistoryModalOpen(false);

  const { dossier_summary, aggregated_identifiers, interaction_history, connection_graph } = dossier || {};

  const latestLocation = useMemo(() => {
    if (!interaction_history || interaction_history.length === 0) {
      return null;
    }

    for (const entry of interaction_history) {
      const recordedAt = entry?.createdAt || entry?.created_at || null;
      const geoDetails = getGeoFromInteraction(entry);

      if (!geoDetails || !geoDetails.query) {
        continue;
      }

      const encodedQuery = encodeURIComponent(geoDetails.query);
      return {
        label: geoDetails.label || geoDetails.query,
        mapQuery: geoDetails.query,
        mapEmbedUrl: `https://maps.google.com/maps?q=${encodedQuery}&z=14&output=embed`,
        mapLinkUrl: `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`,
        recordedAt: recordedAt || null,
        recordedAtFormatted: formatDisplayDate(recordedAt),
        latitude: geoDetails.latitude,
        longitude: geoDetails.longitude,
        city: geoDetails.city,
        region: geoDetails.region,
        country: geoDetails.country,
      };
    }

    return null;
  }, [interaction_history]);

  const ipLocationMap = useMemo(() => {
    if (!interaction_history || interaction_history.length === 0) {
      return {};
    }

    const map = {};

    for (const entry of interaction_history) {
      const ipValue = getIpFromInteraction(entry);
      if (!ipValue) {
        continue;
      }

      const geoDetails = getGeoFromInteraction(entry);
      if (!geoDetails) {
        continue;
      }

      const displayLabel = geoDetails.city
        ? [geoDetails.city, geoDetails.region, geoDetails.country]
            .map((value) => (value ? String(value).trim() : null))
            .filter(Boolean)
            .join(", ")
        : geoDetails.label;

      if (!displayLabel) {
        continue;
      }

      if (!map[ipValue]) {
        map[ipValue] = displayLabel;
      }
    }

    return map;
  }, [interaction_history]);

  const processedGraphData = useMemo(() => {
    if (!connection_graph) return { nodes: [], links: [] };

    const nodesToFilter = connection_graph.nodes
      .filter(node => node.id.includes('fingerprinthashfallback'))
      .map(node => node.id);

    const filteredNodes = connection_graph.nodes.filter(node => !nodesToFilter.includes(node.id));
    const filteredLinks = connection_graph.links.filter(link => 
      !nodesToFilter.includes(link.source) && !nodesToFilter.includes(link.target)
    );

    let deviceCounter = 1;
    const fingerprintMap = {};
    filteredNodes.forEach(node => {
      if (node.group === 'fingerprint') {
        fingerprintMap[node.id] = `Dispositivo ${deviceCounter++}`;
      }
    });

    const renamedNodes = filteredNodes.map(node => {
      if (node.group === 'fingerprint') {
        const displayName = fingerprintMap[node.id] || node.name || node.id;
        return {
          ...node,
          name: displayName,
        };
      }

      if (node.group === 'ip') {
        const locationDisplay = ipLocationMap[node.id];
        const displayName = locationDisplay || node.name || node.id;
        return {
          ...node,
          name: displayName,
          originalValue: node.name || node.id,
        };
      }

      return {
        ...node,
        name: node.name || node.id,
      };
    });

    return { nodes: renamedNodes, links: filteredLinks };
  }, [connection_graph, ipLocationMap]);

  const getNodeColor = useCallback((node) => {
    switch (node.group) {
      case 'fingerprint':
        return '#007bff'; // Blue
      case 'email':
        return '#28a745'; // Green
      case 'phone':
        return '#ffc107'; // Yellow
      default:
        return '#6c757d'; // Gray for others
    }
  }, []);

  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    // VerificaÃ§Ã£o de seguranÃ§a para coordenadas
    if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

    const radius = 28; // Raio do nÃ³
    const label = node.name;
    const fontSize = 12 / globalScale;
    ctx.font = `${fontSize}px Sans-Serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Cor base do nÃ³
    const baseColor = getNodeColor(node);

    // Efeito de vidro (glassmorphism) com a cor do nÃ³
    const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, radius);
    gradient.addColorStop(0, `${baseColor}B3`); // 70% de opacidade
    gradient.addColorStop(1, `${baseColor}33`); // 20% de opacidade

    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
    ctx.fillStyle = gradient;
    ctx.strokeStyle = `${baseColor}80`; // 50% de opacidade para a borda
    ctx.lineWidth = 1 / globalScale;
    ctx.stroke();
    ctx.fill();

    // Cor do texto
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, node.x, node.y);
  }, [getNodeColor]);

  if (loading || !dossier) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '80vh' }}>
          <VuiTypography color="white">{loading ? 'Carregando DossiÃª...' : 'Erro ao carregar o dossiÃª.'}</VuiTypography>
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const displayedHistory = (interaction_history || []).slice(0, 4);

  const renderTimelineItems = (items, isModal = false) => {
    return items.map((item, index) => {
      const eventDate = new Date(item.createdAt);
      const formattedDateTime = `${eventDate.getDate()} ${eventDate.toLocaleString("en-US", { month: "short" }).toUpperCase()} ${eventDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
      let eventData = {};

      switch (item.eventType) {
        case "form_submit":
          eventData = {
            color: item.riskLevel === "Alto Risco" ? "error" : item.riskLevel === "Suspeito" ? "warning" : "success",
            icon: "inventory_2",
            title: "Novo Lead Recebido",
            description: `Um novo lead foi submetido com risco \'${item.riskLevel}\'.`,
            badges: [item.riskLevel, `Score: ${(item.fraudScore * 100).toFixed(0)}%`],
          };
          break;
        case "login":
          eventData = {
            color: "success",
            icon: "login",
            title: "Login bem-sucedido",
            description: "O usuÃ¡rio acessou a plataforma com seguranÃ§a.",
            badges: ["Seguro"],
          };
          break;
        default:
          eventData = {
            color: "info",
            icon: "help",
            title: `Evento: ${item.eventType}`,
            description: "Um evento genÃ©rico foi registrado no sistema.",
            badges: [],
          };
      }

      return (
        <TimelineItem
          key={item.id}
          icon={<Icon>{eventData.icon}</Icon>}
          color={eventData.color}
          title={eventData.title}
          dateTime={formattedDateTime}
          description={eventData.description}
          badges={eventData.badges}
          lastItem={index === items.length - 1}
        />
      );
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleGoBack} sx={{ mr: 1 }}><ArrowBackIcon color="white" /></IconButton>
          <VuiTypography variant="h5" color="white">DossiÃª do Dispositivo</VuiTypography>
        </VuiBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ mb: 3 }}>
              <VuiBox p={3}>
                <PaginatedIdentifierList title="Emails Utilizados" items={aggregated_identifiers?.emails || []} />
                <PaginatedIdentifierList title="Telefones Utilizados" items={aggregated_identifiers?.phones || []} />
              </VuiBox>
            </Card>
            <Card>
              <VuiBox p={3}>
                <TimelineList title="HistÃ³rico Recente" dark>
                  {renderTimelineItems(displayedHistory)}
                </TimelineList>
                {interaction_history && interaction_history.length > 4 && (
                  <VuiBox mt={2} display="flex" justifyContent="center">
                    <VuiButton onClick={handleOpenHistoryModal} color="info" size="small">
                      Ver HistÃ³rico Completo
                    </VuiButton>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 3 }}>
              <VuiBox p={3}>
                <VuiTypography variant="h6" color="white" mb={2}>Grafo de ConexÃµes</VuiTypography>
                <VuiBox sx={{ height: '400px', position: 'relative', backgroundColor: 'transparent', borderRadius: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ForceGraph2D
                    ref={graphRef}
                    graphData={processedGraphData}
                    nodeVal={56} // Tamanho do nÃ³ para a simulaÃ§Ã£o de fÃ­sica
                    linkColor={() => 'rgba(255,255,255,0.2)'}
                    linkDirectionalParticles={1}
                    linkDirectionalParticleSpeed={0.005}
                    linkDirectionalParticleWidth={1.5}
                    nodeCanvasObject={nodeCanvasObject}
                  />
                </VuiBox>
              </VuiBox>
            </Card>
            <Card>
              <VuiBox p={3}>
                 <PaginatedIdentifierList title="EndereÃ§os de IP" items={aggregated_identifiers?.ips || []} />
                 <LocationPreview location={latestLocation} />
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />

      <Dialog 
        open={historyModalOpen} 
        onClose={handleCloseHistoryModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(20, 20, 20, 0.95)', // Fundo semi-transparente escuro
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '15px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            color: 'white',
          }
        }}
      >
        <DialogTitle>
          <Typography color="white" variant="h5">HistÃ³rico Completo</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <TimelineList title="HistÃ³rico Completo" dark>
            {renderTimelineItems(interaction_history || [], true)}
          </TimelineList>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseHistoryModal} sx={{ 
            color: '#3498db', 
            borderColor: '#3498db',
            '&:hover': {
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              borderColor: '#3498db',
            }
          }} variant="outlined">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default DossierDetail;
